import { useEffect, useRef } from "react";

import {
  useBackgroundJobs,
  TrackedJobTaskType,
} from "@/providers/BackgroundJobsProvider";

/**
 * Registers background jobs that an AI SERVER tool submitted (guest export,
 * bulk email send) with the Downloads/progress tracker.
 *
 * Why a bridge: those tools run their `submitJob` server-side via the AI
 * loopback, so — unlike the manual UI flows (PExport/PSendEmail/…) and the
 * FE-owned AI tools (import/translate) — the browser never calls `addJob` and
 * the job stays invisible in the Downloads panel forever (the tracker is pure
 * localStorage + addJob; there is no list-jobs endpoint to reconcile against).
 *
 * The backend now emits a `background_job` SSE frame when such a tool returns
 * `{backgroundJobId, taskType}`; AiAssistantProvider re-broadcasts it as the
 * `veltric:ai-background-job` window event (it has the chat scope's eventId but
 * not the event NAME, and isn't mounted under the tracker's consumers). This
 * component — mounted next to PBackgroundJobs, under BackgroundJobsProvider —
 * does the actual `addJob`. From there the existing polling + download UI take
 * over with zero further changes.
 *
 * Mounted ONCE at the app root (main.tsx), so it works on every route the panel
 * shows on, including the builder routes that live outside the event shell.
 */

type AiBackgroundJobDetail = {
  jobId?: string;
  taskType?: string;
  eventId?: string;
};

// Task types an AI server tool can submit today. Guard the cast so a malformed
// or unexpected frame can't seed the tracker with a bogus taskType (which would
// mis-derive scope/category). Extend this set when a new job-submitting server
// tool is added backend-side.
const AI_TRACKED_TASK_TYPES: ReadonlySet<TrackedJobTaskType> =
  new Set<TrackedJobTaskType>([
    "EXPORT_GUESTS_TO_EXCEL",
    "EXPORT_GUESTS_TO_CSV",
    "SEND_EMAILS",
  ]);

function isTrackedTaskType(value: string): value is TrackedJobTaskType {
  return (AI_TRACKED_TASK_TYPES as ReadonlySet<string>).has(value);
}

export function AiBackgroundJobBridge(): null {
  const { addJob } = useBackgroundJobs();
  // Ref so the listener stays stable (registered once) yet always calls the
  // freshest addJob.
  const addJobRef = useRef(addJob);
  addJobRef.current = addJob;
  // De-dupe: a re-broadcast (e.g. the model re-issuing the tool, or a frame
  // arriving twice) must not add the same jobId twice — duplicate React keys in
  // the panel and a doubled progress row. The tracker itself also guards, but
  // dropping here avoids the churn entirely.
  const seenRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const onJob = (e: Event) => {
      const detail = (e as CustomEvent<AiBackgroundJobDetail>).detail;
      if (!detail?.jobId || !detail.taskType || !detail.eventId) return;
      if (!isTrackedTaskType(detail.taskType)) return;
      if (seenRef.current.has(detail.jobId)) return;
      seenRef.current.add(detail.jobId);
      addJobRef.current({
        jobId: detail.jobId,
        eventId: detail.eventId,
        // The server tool has no cheap event name to hand over; the panel just
        // omits the (cosmetic) event-name link. Polling + download work off
        // jobId + eventId, which are exact.
        eventName: "",
        taskType: detail.taskType,
        // Unknown audience size — the panel reads `progress` (a %) from polling,
        // not totalItems, so 0 is purely a placeholder with no display impact.
        totalItems: 0,
      });
    };
    window.addEventListener("veltric:ai-background-job", onJob);
    return () => window.removeEventListener("veltric:ai-background-job", onJob);
  }, []);

  return null;
}
